import { alpha, Box, Stack, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import type { ElementType } from "react";

interface WearableKpiCardProps {
  label: string;
  unit: string;
  value: string;
  color: string;
  icon: ElementType;
  isSelected: boolean;
  hasData: boolean;
  onClick?: () => void;
}

export function WearableKpiCard({
  label,
  unit,
  value,
  color,
  icon: IconCmp,
  isSelected,
  hasData,
  onClick,
}: WearableKpiCardProps) {
  const canSelectMetric = hasData && !!onClick;
  const labelLine = unit ? `${label} · ${unit}` : label;

  return (
    <Stack
      spacing={0.5}
      onClick={canSelectMetric ? onClick : undefined}
      role={canSelectMetric ? "button" : undefined}
      tabIndex={canSelectMetric ? 0 : undefined}
      onKeyDown={
        canSelectMetric
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        px: 1,
        py: 1,
        minHeight: 80,
        borderRadius: 2,
        cursor: canSelectMetric ? "pointer" : "default",
        opacity: hasData ? 1 : 0.55,
        border: "1px solid",
        borderColor: isSelected ? color : "divider",
        borderLeft: "4px solid",
        borderLeftColor: isSelected ? color : `${color}55`,
        bgcolor: isSelected ? `${color}12` : "background.paper",
        boxShadow: isSelected ? `0 0 0 1px ${color}25` : "none",
        transition:
          "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
        ...(canSelectMetric && {
          "&:hover": {
            bgcolor: isSelected ? `${color}16` : `${color}08`,
            borderColor: color,
            "& .wearable-kpi-chevron": {
              opacity: 0.7,
            },
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            fontSize: "0.72rem",
            fontWeight: isSelected ? 700 : 500,
            lineHeight: 1.25,
            minWidth: 0,
            flex: 1,
          }}
        >
          {labelLine}
        </Typography>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: alpha(color, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconCmp sx={{ color, fontSize: 15 }} />
        </Box>
        {canSelectMetric && isSelected ? (
          <ChevronRight
            className="wearable-kpi-chevron"
            sx={{ fontSize: 18, color, opacity: 0.7, flexShrink: 0 }}
          />
        ) : null}
      </Stack>

      <Typography
        sx={{
          fontSize: isSelected ? "1.5rem" : "1.35rem",
          fontWeight: 800,
          lineHeight: 1.1,
          color: hasData ? color : "text.disabled",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
